import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  FileText,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Link } from "wouter";

const policyFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  code: z.string().optional(),
  policyType: z.string().default("vacation"),
  description: z.string().optional(),
  accrualType: z.string().default("annual"),
  accrualRate: z.string().optional(),
  accrualUnit: z.string().default("hours"),
  maxBalance: z.string().optional(),
  carryoverLimit: z.string().optional(),
  carryoverExpireMonths: z.string().optional(),
  waitingPeriodDays: z.string().optional(),
  allowNegativeBalance: z.boolean().default(false),
  requiresApproval: z.boolean().default(true),
  minRequestIncrement: z.string().optional(),
  maxConsecutiveDays: z.string().optional(),
  advanceNoticeDays: z.string().optional(),
  isActive: z.boolean().default(true),
  isPaid: z.boolean().default(true),
  color: z.string().optional(),
});

type PolicyFormData = z.infer<typeof policyFormSchema>;

interface TimeOffPolicy {
  id: number;
  clientId: number;
  name: string;
  code?: string;
  policyType?: string;
  description?: string;
  accrualType: string;
  accrualRate?: string;
  accrualUnit: string;
  maxBalance?: string;
  carryoverLimit?: string;
  carryoverExpireMonths?: number;
  waitingPeriodDays?: number;
  allowNegativeBalance?: boolean;
  requiresApproval?: boolean;
  minRequestIncrement?: string;
  maxConsecutiveDays?: number;
  advanceNoticeDays?: number;
  isActive?: boolean;
  isPaid?: boolean;
  color?: string;
  sortOrder?: number;
}

const POLICY_TYPES = [
  { value: "vacation", label: "Vacation" },
  { value: "sick", label: "Sick Leave" },
  { value: "personal", label: "Personal Time" },
  { value: "pto", label: "PTO (Combined)" },
  { value: "fmla", label: "FMLA" },
  { value: "bereavement", label: "Bereavement" },
  { value: "jury_duty", label: "Jury Duty" },
  { value: "parental", label: "Parental Leave" },
  { value: "military", label: "Military Leave" },
  { value: "unpaid", label: "Unpaid Leave" },
  { value: "other", label: "Other" },
];

const ACCRUAL_TYPES = [
  { value: "annual", label: "Annual (once per year)" },
  { value: "monthly", label: "Monthly" },
  { value: "bi_weekly", label: "Bi-Weekly" },
  { value: "weekly", label: "Weekly" },
  { value: "hourly", label: "Per Hour Worked" },
  { value: "none", label: "No Accrual (fixed amount)" },
];

const ACCRUAL_UNITS = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

const POLICY_COLORS = [
  { value: "#3B82F6", label: "Blue" },
  { value: "#10B981", label: "Green" },
  { value: "#F59E0B", label: "Yellow" },
  { value: "#EF4444", label: "Red" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#EC4899", label: "Pink" },
  { value: "#6B7280", label: "Gray" },
];

export default function TimeOffPolicies() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<TimeOffPolicy | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data: policies, isLoading } = useQuery<TimeOffPolicy[]>({
    queryKey: ["/api/time-off/policies"],
  });

  const form = useForm<PolicyFormData>({
    resolver: zodResolver(policyFormSchema),
    defaultValues: {
      name: "",
      code: "",
      policyType: "vacation",
      description: "",
      accrualType: "annual",
      accrualRate: "",
      accrualUnit: "hours",
      maxBalance: "",
      carryoverLimit: "",
      carryoverExpireMonths: "",
      waitingPeriodDays: "0",
      allowNegativeBalance: false,
      requiresApproval: true,
      minRequestIncrement: "0.5",
      maxConsecutiveDays: "",
      advanceNoticeDays: "0",
      isActive: true,
      isPaid: true,
      color: "#3B82F6",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: PolicyFormData) => {
      return apiRequest("POST", "/api/time-off/policies", {
        ...data,
        carryoverExpireMonths: data.carryoverExpireMonths ? parseInt(data.carryoverExpireMonths) : null,
        waitingPeriodDays: data.waitingPeriodDays ? parseInt(data.waitingPeriodDays) : 0,
        maxConsecutiveDays: data.maxConsecutiveDays ? parseInt(data.maxConsecutiveDays) : null,
        advanceNoticeDays: data.advanceNoticeDays ? parseInt(data.advanceNoticeDays) : 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/metrics"] });
      toast({ title: "Policy created", description: "The time off policy has been created." });
      handleClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create policy.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PolicyFormData }) => {
      return apiRequest("PATCH", `/api/time-off/policies/${id}`, {
        ...data,
        carryoverExpireMonths: data.carryoverExpireMonths ? parseInt(data.carryoverExpireMonths) : null,
        waitingPeriodDays: data.waitingPeriodDays ? parseInt(data.waitingPeriodDays) : 0,
        maxConsecutiveDays: data.maxConsecutiveDays ? parseInt(data.maxConsecutiveDays) : null,
        advanceNoticeDays: data.advanceNoticeDays ? parseInt(data.advanceNoticeDays) : 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/policies"] });
      toast({ title: "Policy updated", description: "The time off policy has been updated." });
      handleClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update policy.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/time-off/policies/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/metrics"] });
      toast({ title: "Policy deleted", description: "The time off policy has been deleted." });
      setDeleteConfirmId(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete policy.", variant: "destructive" });
    },
  });

  const handleOpen = (policy?: TimeOffPolicy) => {
    if (policy) {
      setEditingPolicy(policy);
      form.reset({
        name: policy.name,
        code: policy.code || "",
        policyType: policy.policyType || "vacation",
        description: policy.description || "",
        accrualType: policy.accrualType,
        accrualRate: policy.accrualRate || "",
        accrualUnit: policy.accrualUnit,
        maxBalance: policy.maxBalance || "",
        carryoverLimit: policy.carryoverLimit || "",
        carryoverExpireMonths: policy.carryoverExpireMonths?.toString() || "",
        waitingPeriodDays: policy.waitingPeriodDays?.toString() || "0",
        allowNegativeBalance: policy.allowNegativeBalance || false,
        requiresApproval: policy.requiresApproval ?? true,
        minRequestIncrement: policy.minRequestIncrement || "0.5",
        maxConsecutiveDays: policy.maxConsecutiveDays?.toString() || "",
        advanceNoticeDays: policy.advanceNoticeDays?.toString() || "0",
        isActive: policy.isActive ?? true,
        isPaid: policy.isPaid ?? true,
        color: policy.color || "#3B82F6",
      });
    } else {
      setEditingPolicy(null);
      form.reset();
    }
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingPolicy(null);
    form.reset();
  };

  const onSubmit = (data: PolicyFormData) => {
    if (editingPolicy) {
      updateMutation.mutate({ id: editingPolicy.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/time-off">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Time Off Policies</h1>
            <p className="text-gray-500">Configure different types of time off</p>
          </div>
        </div>
        <Button onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Policy
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : !policies || policies.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
              <p>No time off policies configured</p>
              <Button variant="link" onClick={() => handleOpen()}>
                Create your first policy
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {policies.map((policy) => (
                <div
                  key={policy.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: policy.color || "#6B7280" }}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{policy.name}</h3>
                        {policy.code && (
                          <Badge variant="outline" className="text-xs">
                            {policy.code}
                          </Badge>
                        )}
                        {policy.policyType && (
                          <Badge variant="secondary" className="text-xs capitalize">
                            {policy.policyType.replace("_", " ")}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {policy.accrualType === "none"
                          ? "No accrual"
                          : `${policy.accrualRate || 0} ${policy.accrualUnit} per ${policy.accrualType.replace("_", "-")}`}
                        {policy.maxBalance && ` • Max: ${policy.maxBalance} ${policy.accrualUnit}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={policy.isActive ? "default" : "secondary"}>
                      {policy.isActive ? "Active" : "Inactive"}
                    </Badge>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpen(policy)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteConfirmId(policy.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPolicy ? "Edit Policy" : "Create Policy"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Policy Name <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Vacation" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., VAC" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="policyType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Policy Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {POLICY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The category of this time off policy
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Policy description..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="accrualType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accrual Type</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ACCRUAL_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="accrualRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accrual Rate</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="80" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="accrualUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ACCRUAL_UNITS.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="maxBalance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum Balance</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="120" {...field} />
                      </FormControl>
                      <FormDescription>Leave empty for no cap</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="carryoverLimit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Carryover Limit</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="40" {...field} />
                      </FormControl>
                      <FormDescription>Max hours/days to carry over</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="waitingPeriodDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Waiting Period (Days)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="90" {...field} />
                      </FormControl>
                      <FormDescription>Days before new hires can accrue</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Color</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-4 h-4 rounded-full"
                                  style={{ backgroundColor: field.value }}
                                />
                                {POLICY_COLORS.find((c) => c.value === field.value)?.label || "Select color"}
                              </div>
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {POLICY_COLORS.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-4 h-4 rounded-full"
                                  style={{ backgroundColor: color.value }}
                                />
                                {color.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="requiresApproval"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Requires Approval</FormLabel>
                        <FormDescription>Requests must be approved by manager</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="allowNegativeBalance"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Allow Negative Balance</FormLabel>
                        <FormDescription>Employees can request more than available</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Active</FormLabel>
                        <FormDescription>Policy is available for use</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isPaid"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Paid Time Off</FormLabel>
                        <FormDescription>Employees are paid during time off</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : editingPolicy
                    ? "Update Policy"
                    : "Create Policy"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Policy</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this policy? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
